import React, { useCallback, useMemo, type FC } from 'react';
import { CouponInformationTestId, type CouponInformationProps } from './coupon-information.types';
import { cn } from '../../utils/cn';
import Button from '../../atoms/button';
import Text from '../../atoms/text';
import { FlatList } from '../../molecules/flat-list';
import { MyCouponCard } from '../../molecules/my-coupon-card';
import { CouponDetail } from './coupon-detail';

import styles from './coupon-information.module.css';

export const CouponInformation: FC<CouponInformationProps> = ({
    coupon,
    handleAgree,
    content,
    label,
    expiredLabel,
    className,
    hasShowProgress,
}) => {
    const dummyData = [{ id: 'content' }];

    const couponCardData = useMemo(
        () => ({
            name: coupon.name ?? coupon.discount ?? '',
            tag: coupon.description ?? coupon.condition ?? '',
            imageUri: coupon.brand_image ?? '',
            type: coupon.type ?? '',
            salesrule: {
                to_date: coupon.endDate ?? '',
                usage_limit: coupon.usage_limit ?? 0,
                times_used: coupon.times_used ?? 0,
            },
        }),
        [coupon]
    );

    const progressLabel = useMemo(() => {
        const usageLimit = coupon.usage_limit ?? 0;
        const timesUsed = coupon.times_used ?? 0;

        if (!usageLimit) return 'Đã dùng 0%, ';

        const usedPercent = Math.min(100, Math.round((timesUsed / usageLimit) * 100));
        return `Đã dùng ${usedPercent}%, `;
    }, [coupon.usage_limit, coupon.times_used]);
    const renderHeader = useCallback(
        () => (
            <div data-testid={CouponInformationTestId.HEADER_BACKGROUND} className={styles.containerHeader}>
                <div data-testid={CouponInformationTestId.COUPON_CARD_VIEW} className={styles.viewCouponCard}>
                    <div className={styles.headerBackground} />
                    <MyCouponCard
                        variant="detail"
                        coupon={couponCardData}
                        expiredLabel={expiredLabel}
                        hasShowProgress={hasShowProgress}
                        progressLabel={progressLabel}
                        testID="coupon-information-detail-card"
                        className={styles.headerCouponInfo}
                    />
                </div>
            </div>
        ),
        [couponCardData, expiredLabel, progressLabel, hasShowProgress]
    );

    const renderItem = useCallback(
        () => (
            <div data-testid={CouponInformationTestId.DETAIL_CONTAINER} className={styles.detailContainer}>
                <CouponDetail content={content} coupon={coupon} />
            </div>
        ),
        [content, coupon]
    );

    return (
        <div className={cn(styles.container, className)} data-testid={CouponInformationTestId.CONTAINER}>
            <FlatList
                data={dummyData}
                renderItem={renderItem}
                keyExtractor={item => item.id}
                ListHeaderComponent={renderHeader}
                showsVerticalScrollIndicator={false}
                className={styles.flatListContent}
            />
            <div className={styles.buttonContainer}>
                <Button testID={CouponInformationTestId.AGREE_BUTTON} onPress={handleAgree} className={styles.button}>
                    <Text className={styles.textButton}>{label}</Text>
                </Button>
            </div>
        </div>
    );
};
